import React, { useState, useEffect } from 'react';
import {
  UserCircle,
  Home,
  Building2,
  Eye,
  Pencil,
  MessageCircle,
  Info,
  LogOut,
  Sparkles,
  CreditCard,
  UserPlus,
  Award,
  BarChart3,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  Github,
  Search,
  Receipt,
  Medal,
} from 'lucide-react';
import { FaQuestionCircle } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../Config/config';
import { hasTsgAiAccess, TSG_AI_URL } from '../utils/tsgAiAccess';
import { isCollegeChallengeEnabled } from '../utils/collegeChallengeAccess';

/**
 * CollegeHomeSideBar
 *
 * Enterprise-grade navigation sidebar for college admin users.
 * Collapsible: expanded (w-64) â†” collapsed (icon-only, w-[68px]).
 * Because this sidebar is in normal document flow (not fixed), the
 * adjacent flex-1 content area automatically reflows when width changes.
 */
const CollegeHomeSideBar = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const username = user?.username || user?.userId || user?.id;

  const [expanded, setExpanded]           = useState(true);
  const [searchQuery, setSearchQuery]     = useState('');

  const [collegeChallengeEnabled, setCollegeChallengeEnabled] = useState(isCollegeChallengeEnabled);

  // Sync expanded state to document for global layout adjustments
  useEffect(() => {
    document.documentElement.setAttribute('data-college-sidebar-expanded', expanded.toString());
  }, [expanded]);

  const [openDropdown, setOpenDropdown]   = useState('');
  const [hasTsgAi, setHasTsgAi]           = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [hasPurchasedCredits, setHasPurchasedCredits] = useState(true); // Default true until checked

  useEffect(() => {
    const checkAccess = async () => {
      if (!username) return;

      try {
        // Check TSG AI Access
        const tsgAiAccess = await hasTsgAiAccess(username);
        setHasTsgAi(tsgAiAccess.hasAccess);

        // Check Credit Purchase Status + resolve college_id
        const collegeRes = await axios.get(`${API_BASE_URL}/colleges/username/${username}`);
        const collegeData = collegeRes.data;
        const college_id = collegeData?.id || collegeData?._id || collegeData?.college_id;

        if (college_id) {
          // Credit access
          const purchasedCount = parseInt(collegeData.total_credits_purchased, 10) || 0;
          setHasPurchasedCredits(purchasedCount > 0);

          // College Challenge: re-verify from API when localStorage shows false.
          // This self-heals stale cache from logins before the feature launched.
          if (!isCollegeChallengeEnabled()) {
            try {
              const eligRes = await axios.get(
                `${API_BASE_URL}/api/college-challenge/check-eligibility?college_id=${college_id}`
              );
              if (eligRes.data?.eligible) {
                localStorage.setItem('collegeChallengeEnabled', 'true');
                setCollegeChallengeEnabled(true);
              }
            } catch {
              // Non-critical — leave existing state unchanged on error
            }
          }
        }
      } catch (error) {
        console.error('Error checking college access status:', error);
      }
    };

    checkAccess();
  }, [username]);

  const isDashboard = location.pathname === `/collegehome/${username}`;

  // Show minimized sidebar if not on dashboard, otherwise expanded
  useEffect(() => {
    if (!isDashboard) {
      setExpanded(false);
    } else {
      setExpanded(true);
    }
  }, [isDashboard]);

  const toggleDropdown = (label) => {
    if (!expanded) {
      // Expand the sidebar first, then open the dropdown
      setExpanded(true);
      setOpenDropdown(label);
    } else {
      setOpenDropdown((prev) => (prev === label ? '' : label));
    }
  };

  const confirmLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate('/');
  };

  const isPathActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const menuItems = [
    {
      label: 'Dashboard',
      icon: <Home size={16} />,
      onClick: () => navigate(`/collegehome/${username}`),
      path: `/collegehome/${username}`,
      restricted: true,
      order: 1,
    },
    {
      label: 'College Profile',
      icon: <Building2 size={16} />,
      subItems: [
        { label: 'View Profile', icon: <Eye size={14} />,    path: '/CollegeViewProfile' },
        { label: 'Edit Profile', icon: <Pencil size={14} />, path: '/CollegeEditProfile' },
      ],
      order: 2,
    },
    {
      label: 'Students',
      icon: <UserCircle size={16} />,
      subItems: [
        { label: 'Student Management', icon: <UserPlus size={14} />, path: '/college/student-management' },
      ],
      restricted: true,
      order: 3,
    },
    ...(collegeChallengeEnabled
      ? [{
          label: 'College Challenge',
          icon: <Award size={16} />,
          onClick: () => navigate('/college-challenge'),
          path: '/college-challenge',
          badge: 'NEW',
          order: 4,
        }]
      : []),
    {
      label: 'Certificates',
      icon: <Medal size={16} />,
      subItems: [
        { label: 'Certificate Management', icon: <Medal size={14} />, path: '/college/certificates' },
      ],
      restricted: true,
      order: 5,
    },
    {
      label: 'Analytics',
      icon: <BarChart3 size={16} />,
      onClick: () => navigate('/college-analytics'),
      path: '/college-analytics',
      restricted: true,
      order: 6,
    },
    {
      label: 'Purchase Credits',
      icon: <CreditCard size={16} />,
      subItems: [
        { label: 'Purchase Plans',      icon: <CreditCard size={14} />, path: '/college/purchase-credits' },
        { label: 'Transaction History', icon: <Receipt size={14} />,    path: '/college/transaction-history' },
      ],
      isHighlighted: true,
      order: 7,
    },
    ...(hasTsgAi
      ? [
          {
            label: 'TSG AI',
            icon: <Sparkles size={16} />,
            isHighlighted: true,
            restricted: true,
            order: 8,
            onClick: async () => {
              try {
                const user = JSON.parse(localStorage.getItem('user'));
                const userId = user?.userId || user?.id || user?.user_id;
                if (userId) {
                  const access = await hasTsgAiAccess(userId);
                  if (access.hasAccess) {
                    window.open(TSG_AI_URL, '_blank', 'noopener,noreferrer');
                  } else {
                    navigate('/skillgenpayments');
                  }
                }
              } catch {
                navigate('/skillgenpayments');
              }
            },
          },
        ]
      : []),
    {
      label: 'Support',
      icon: <MessageCircle size={16} />,
      subItems: [
        { label: 'Chat Support', icon: <FaQuestionCircle size={13} />, path: '/support-college' },
      ],
      order: 9,
    },
    {
      label: 'GitHub',
      icon: <Github size={16} />,
      onClick: () => navigate('/college/github'),
      path: '/college/github',
      restricted: true,
      order: 10,
    },
    {
      label: 'About Portal',
      icon: <Info size={16} />,
      onClick: () => navigate('/collegeabout'),
      path: '/collegeabout',
      order: 11,
    },
  ]
    .filter(item => !item.restricted || hasPurchasedCredits)
    .sort((a, b) => {
      if (hasPurchasedCredits) {
        return a.order - b.order;
      }
      // If not purchased, we want a specific order:
      // Profile, Challenge (eligible only), Purchase, Support, About
      const unpurchasedOrder = [
        'College Profile',
        ...(collegeChallengeEnabled ? ['College Challenge'] : []),
        'Purchase Credits',
        'Support',
        'About Portal',
      ];
      return unpurchasedOrder.indexOf(a.label) - unpurchasedOrder.indexOf(b.label);
    });

  /* ------- render ------- */
  return (
    <>
      <aside
        className="flex flex-col fixed left-0 z-40"
        style={{
          top: '61px',
          height: 'calc(100vh - 61px)',
          width: expanded ? '256px' : '68px',
          transition: 'width 300ms ease',
          background: '#843FEF',
          borderRight: '1px solid rgba(255,255,255,0.1)',
          boxShadow: 'var(--shadow-xs)',
          color: '#FFFFFF'
        }}
      >
        {/* -- Nav items -- */}
        <nav className="flex flex-col gap-0.5 px-2 py-3 flex-1 overflow-y-auto overflow-x-visible custom-scrollbar">
          {/* -- Search + Close row -- */}
          <div
            className={`flex flex-shrink-0 items-center mb-1 pb-2 gap-2 ${expanded ? 'px-1' : 'justify-center'}`}
            style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}
          >
            {expanded && (
              <div className="relative flex-1">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/60 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-7 pr-3 py-1.5 text-xs rounded-lg bg-white/10 border border-transparent focus:border-white/30 focus:outline-none text-white placeholder:text-white/50 transition-colors"
                />
              </div>
            )}
            <button
              onClick={() => { setExpanded((v) => !v); if (expanded) setSearchQuery(''); }}
              className="p-1.5 rounded-lg hover:bg-white/10 active:scale-95 transition-colors flex-shrink-0 relative group"
              aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
            >
              {expanded
                ? <X size={18} className="text-white/60" />
                : <Menu size={20} className="text-white/60" />
              }

              {/* Custom Tooltip */}
              <div className="absolute left-full top-1/2 -translate-y-1/2 ml-3 px-2 py-1 bg-gray-900 text-white text-[10px] font-bold rounded shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-[100] pointer-events-none">
                {expanded ? 'Collapse' : 'Expand'}
                <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900" />
              </div>
            </button>
          </div>
          {menuItems.filter(({ label }) =>
            !searchQuery || label.toLowerCase().includes(searchQuery.toLowerCase())
          ).map(({ label, icon, subItems, onClick, isHighlighted, path }) => {
            const isOpen = openDropdown === label;
            const groupActive = path
              ? isPathActive(path)
              : subItems?.some((s) => isPathActive(s.path)) ?? false;

            return (
              <div key={label}>
                <button
                  onClick={() => {
                    if (onClick) {
                      onClick();
                      setExpanded(false);
                    } else if (subItems?.length) {
                      toggleDropdown(label);
                    }
                  }}
                  className={`
                    flex items-center gap-2.5 w-full text-left rounded-xl relative
                    transition-all duration-150 text-sm font-semibold group
                    ${expanded ? 'px-3 py-2.5 justify-between' : 'px-0 py-2.5 justify-start pl-[22px]'}
                    ${groupActive
                      ? 'bg-white/20 text-white'
                      : isHighlighted
                        ? 'bg-white text-[#843FEF]'
                        : 'text-white/80 hover:bg-white/10 hover:text-white'
                    }
                    group
                  `}
                  style={
                    groupActive
                      ? {
                          paddingLeft: expanded ? '12px' : undefined,
                        }
                      : {}
                  }
                >
                  <div className="flex items-center">
                    <span
                      className="flex-shrink-0 opacity-80 h-[18px] w-[18px] flex items-center justify-center"
                    >
                      {icon}
                    </span>
                    {expanded && <span className="ml-2.5 leading-none truncate">{label}</span>}
                    {expanded && menuItems.find(item => item.label === label)?.badge && (
                      <span className="ml-2 px-1.5 py-0.5 text-[10px] bg-red-500 text-white rounded-full font-bold">
                        {menuItems.find(item => item.label === label).badge}
                      </span>
                    )}
                  </div>

                  {expanded && subItems?.length > 0 && (
                    <ChevronDown
                      size={13}
                      className={`transition-transform duration-200 opacity-60 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
                    />
                  )}

                  {/* Custom Tooltip */}
                  <div className="absolute left-full top-1/2 -translate-y-1/2 ml-3 px-2 py-1 bg-gray-900 text-white text-[10px] font-bold rounded shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-[100] pointer-events-none">
                    {label}
                    {/* Arrow */}
                    <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900" />
                  </div>
                </button>

                {expanded && isOpen && subItems?.length > 0 && (
                  <div
                    className="ml-8 mt-1 mb-1 flex flex-col gap-0.5 pl-3 border-l-2 border-white/20 animate-fadeIn"
                  >
                    {subItems.map(({ label: subLabel, path: subPath, icon: subIcon }) => {
                      const subActive = isPathActive(subPath);
                      return (
                        <button
                          key={subLabel}
                          onClick={() => {
                            navigate(subPath);
                            setExpanded(false);
                          }}
                          className={`
                            flex items-center gap-2 text-left text-xs py-1.5 px-2 rounded-lg transition-all font-medium relative group
                            ${subActive
                              ? 'text-white font-bold bg-white/10'
                              : 'text-white/70 hover:text-white hover:bg-white/5'
                            }
                          `}
                        >
                          <span className="opacity-60 flex-shrink-0">{subIcon}</span>
                          {subLabel}

                          {/* Custom Tooltip */}
                          <div className="absolute left-full top-1/2 -translate-y-1/2 ml-3 px-2 py-1 bg-gray-900 text-white text-[10px] font-bold rounded shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-[100] pointer-events-none">
                            {subLabel}
                            <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900" />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* -- Logout -- */}
        <div
          className="px-2 pb-3 pt-2 flex-shrink-0"
          style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}
        >
          <button
            onClick={() => setShowLogoutModal(true)}
            className={`flex items-center w-full rounded-xl text-sm font-semibold text-white/80 hover:bg-red-500/20 hover:text-white transition-all relative group
              ${expanded ? 'gap-3 px-3 py-2.5' : 'justify-start px-[22px] py-2.5'}`}
          >
            <LogOut size={15} className="flex-shrink-0" />
            {expanded && <span className="leading-none">Sign Out</span>}

            {/* Custom Tooltip */}
            <div className="absolute left-full top-1/2 -translate-y-1/2 ml-3 px-2 py-1 bg-gray-900 text-white text-[10px] font-bold rounded shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-[100] pointer-events-none">
              Sign Out
              <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900" />
            </div>
          </button>
        </div>

        {/* theskillgenie.com link */}
        <div 
           className={`px-2 pb-4 text-center ${expanded ? "block" : "hidden"}`}
        >
          <a 
            href="https://www.theskillgenie.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-[10px] font-bold text-white/40 hover:text-white/80 transition-colors uppercase tracking-[0.2em]"
          >
            theskillgenie.com
          </a>
        </div>
      </aside>

      {/* -- Logout confirmation modal -- */}
      {showLogoutModal && (
        <div
          className="fixed inset-0 flex justify-center items-center z-[100] p-4"
          style={{ background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)' }}
        >
          <div
            className="bg-bgSection rounded-2xl p-8 text-center w-full max-w-sm animate-brand-fadeInUp"
            style={{
              border: '1px solid var(--border-subtle)',
              boxShadow: 'var(--shadow-xl)',
            }}
          >
            <div className="w-12 h-12 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-5">
              <LogOut size={22} className="text-red-500" />
            </div>
            <h2 className="text-lg font-bold text-heading mb-1.5">Sign Out?</h2>
            <p className="text-sm text-muted mb-6 leading-relaxed">
              You will be signed out of the college admin portal.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLogoutModal(false)}
                className="flex-1 py-2.5 rounded-xl font-semibold text-sm text-muted hover:bg-bgSoft transition"
                style={{ border: '1px solid var(--border-subtle)' }}
              >
                Cancel
              </button>
              <button
                onClick={confirmLogout}
                className="flex-1 py-2.5 bg-red-500 text-white rounded-xl font-semibold text-sm hover:bg-red-600 transition active:scale-95"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CollegeHomeSideBar;